/*
 * Sticky header behaviour
 *
 * - `.is-scrolled` once the header is stuck, which fades out its solid layer
 *   to show the frosted one. Browsers with scroll-driven animations ramp this
 *   in CSS instead, so the class only matters as a fallback there.
 * - `.is-hidden` while scrolling down, removed when scrolling back up. The
 *   header keeps its height either way, so nothing below it moves.
 * - Publishes --header-h and --header-offset for sticky elements below it
 *   (see `.below-header`) and for the mobile menu's max height.
 *
 * Styles: src/scss/utilities/motion.scss. Rules: .claude/docs/MOTION.md.
 */

const header = document.querySelector(".main-header");

if (header) {
	const root = document.documentElement;
	const panel = header.querySelector(".secondary");
	const readbar = document.querySelector(".article-readbar");
	const mobile = window.matchMedia("(max-width: 639.98px)");
	const canHover = window.matchMedia("(hover: hover)");
	const TRAVEL = 8; // px of scroll before a change of direction counts
	const ANCHOR_HOLD = 1000; // ms to keep the header out of the way after an in-page jump

	let height = header.offsetHeight;
	let hidden = false;
	let lastY = Math.max(0, window.scrollY);
	let ticking = false;
	let holdHiddenUntil = 0;

	const publishOffset = () => {
		root.style.setProperty("--header-offset", hidden ? "0px" : `${height}px`);
	};

	const setHidden = (next) => {
		if (next === hidden) return;
		hidden = next;
		header.classList.toggle("is-hidden", hidden);
		publishOffset();
	};

	const menuOpen = () => mobile.matches && panel && !panel.classList.contains("hidden");

	const update = () => {
		ticking = false;
		// Clamp so iOS overscroll bounce at either end doesn't read as a direction change.
		const y = Math.min(Math.max(window.scrollY, 0), root.scrollHeight - window.innerHeight);
		const travel = y - lastY;
		if (Math.abs(travel) < TRAVEL) return;
		lastY = y;

		// On articles the reading bar takes over the top of the screen.
		if (readbar && readbar.classList.contains("is-visible")) return setHidden(true);
		if (menuOpen() || header.matches(":focus-within") || (canHover.matches && header.matches(":hover"))) return setHidden(false);
		// After an in-page link, keep the header from covering the target, even on an upward jump.
		if (performance.now() < holdHiddenUntil && y > height) return setHidden(true);
		setHidden(travel > 0 && y > height);
	};

	window.addEventListener(
		"scroll",
		() => {
			if (ticking) return;
			ticking = true;
			requestAnimationFrame(update);
		},
		{ passive: true }
	);

	document.addEventListener("click", (event) => {
		const link = event.target.closest && event.target.closest('a[href^="#"]');
		if (link && link.getAttribute("href").length > 1 && !header.contains(link)) {
			holdHiddenUntil = performance.now() + ANCHOR_HOLD;
		}
	});

	header.addEventListener("focusin", () => setHidden(false));

	new ResizeObserver(() => {
		height = header.offsetHeight;
		root.style.setProperty("--header-h", `${height}px`);
		publishOffset();
	}).observe(header);

	// A 1px sentinel just above the header: once it has scrolled out of view,
	// the header is stuck to the top.
	const sentinel = document.createElement("div");
	sentinel.setAttribute("aria-hidden", "true");
	sentinel.style.cssText = "height:1px;margin-bottom:-1px;pointer-events:none;";
	header.before(sentinel);
	new IntersectionObserver(([entry]) => {
		header.classList.toggle("is-scrolled", !entry.isIntersecting && entry.boundingClientRect.top < 0);
	}).observe(sentinel);
}
